import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useLista } from '../api/lista';
import type { Dostawca, Indeks, Lista, Partia } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  AtrapaLeku, Karta, Komunikat, Ladowanie, Plakietka, Pole, PoleTekstowe,
  Przycisk, Pusto, Wybor, Zakladki,
} from '../components/ui';
import { PasekWspolpracy } from '../components/PasekWspolpracy';
import { nazwaZListy, useEdycjaWspolna } from '../lib/wspolna-edycja';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { data as fData, zlote } from '../lib/format';

type Okno = 'wszystkie' | '30' | '60' | '90' | 'przeterminowane';

/** Ile dni zostało do terminu — liczone po dniach kalendarzowych, nie po godzinach. */
function dniDoTerminu(termin: string | null): number | null {
  if (!termin) return null;
  const dzis = new Date(); dzis.setHours(0, 0, 0, 0);
  const koniec = new Date(termin); koniec.setHours(0, 0, 0, 0);
  return Math.round((koniec.getTime() - dzis.getTime()) / 86_400_000);
}

function Termin({ termin }: { termin: string | null }) {
  const dni = dniDoTerminu(termin);
  if (dni === null) return <span className="text-kamien-400">bez terminu</span>;
  if (dni < 0) return <Plakietka ton="blad">po terminie od {Math.abs(dni)} dni</Plakietka>;
  if (dni <= 30) return <Plakietka ton="blad">{dni} dni</Plakietka>;
  if (dni <= 90) return <Plakietka ton="uwaga">{dni} dni</Plakietka>;
  return <span className="text-kamien-600">{dni} dni</span>;
}

export function Partie() {
  const { maUprawnienie } = useAuth();
  const mozeEdytowac = maUprawnienie('kartoteka.zapis');
  const widziCeny = maUprawnienie('ceny.odczyt');
  const [okno, setOkno] = useState<Okno>('wszystkie');
  const [szukaj, setSzukaj] = useState('');
  const [edytowana, setEdytowana] = useState<Partia | 'nowa' | null>(null);

  const parametry = new URLSearchParams({
    ...(szukaj ? { szukaj } : {}),
    ...(okno === 'przeterminowane' ? { tylkoPrzeterminowane: 'true' } : {}),
    ...(okno !== 'wszystkie' && okno !== 'przeterminowane' ? { waznoscDoDni: okno } : {}),
    naStronie: '100',
  });

  const lista = useQuery({
    queryKey: ['partie', parametry.toString()],
    queryFn: () => api.get<Lista<Partia>>(`/partie?${parametry}`),
    // Terminy ważności to rzecz, którą chce się widzieć aktualną bez F5.
    refetchInterval: 60_000,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Partie i terminy ważności</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Rozchód pójdzie według FEFO — najpierw partia z najkrótszym terminem. Ilości pojawią się razem z ruchami magazynowymi.
          </p>
        </div>
        <PrzyciskExcel dzial="partie" parametry={parametry} className="ml-auto" />
        {mozeEdytowac && (
          <Przycisk wariant="glowny" onClick={() => setEdytowana('nowa')}>Nowa partia</Przycisk>
        )}
      </div>

      <Zakladki
        wartosc={okno} zmien={setOkno}
        opcje={[
          { wartosc: 'wszystkie', etykieta: 'Wszystkie' },
          { wartosc: '30', etykieta: 'Kończą się w 30 dni' },
          { wartosc: '60', etykieta: '60 dni' },
          { wartosc: '90', etykieta: '90 dni' },
          { wartosc: 'przeterminowane', etykieta: 'Po terminie' },
        ]}
      />

      <div className="max-w-md">
        <Pole etykieta="Szukaj" value={szukaj} onChange={(e) => setSzukaj(e.target.value)}
          placeholder="Numer serii, nazwa albo kod materiału" />
      </div>

      {edytowana && (
        <FormularzPartii
          key={edytowana === 'nowa' ? 'nowa' : edytowana.id}
          partia={edytowana === 'nowa' ? null : edytowana}
          widziCeny={widziCeny}
          zamknij={() => setEdytowana(null)}
        />
      )}

      {lista.isLoading ? <Ladowanie /> : (
        <Karta>
          {lista.data && lista.data.pozycje.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-sm">
                <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Materiał</th>
                    <th className="px-5 py-3 font-medium">Seria</th>
                    <th className="px-5 py-3 font-medium">Termin ważności</th>
                    <th className="px-5 py-3 font-medium">Zostało</th>
                    <th className="px-5 py-3 font-medium">Dostawca</th>
                    {widziCeny && <th className="px-5 py-3 text-right font-medium">Koszt jedn.</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamien-200">
                  {lista.data.pozycje.map((p) => (
                    <tr key={p.id}
                      onClick={() => mozeEdytowac && setEdytowana(p)}
                      className={mozeEdytowac ? 'cursor-pointer hover:bg-kamien-50' : ''}>
                      <td className="px-5 py-3">
                        <span className="font-mono text-xs text-kamien-500">{p.item.code}</span>
                        <span className="ml-2 text-kamien-900">{p.item.name}</span>
                        {p.item.isDrugDummy && <span className="ml-2"><AtrapaLeku /></span>}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-kamien-700">{p.lotNumber ?? '—'}</td>
                      <td className="px-5 py-3 text-kamien-700">{fData(p.expiryDate)}</td>
                      <td className="px-5 py-3"><Termin termin={p.expiryDate} /></td>
                      <td className="px-5 py-3 text-xs text-kamien-500">{p.supplier?.name ?? '—'}</td>
                      {widziCeny && (
                        <td className="px-5 py-3 text-right text-kamien-700">{zlote(p.unitCost)}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="border-t border-kamien-200 px-5 py-3 text-sm text-kamien-500">
                {lista.data.razem} partii · kolejność FEFO
              </p>
            </div>
          ) : (
            <Pusto
              tytul={okno === 'wszystkie' ? 'Brak partii' : 'Nic w tym oknie czasowym'}
              opis={okno === 'wszystkie'
                ? 'Partie zakłada się dla materiałów z włączoną obsługą terminów ważności.'
                : 'Żadna partia nie kończy się w wybranym okresie — to dobra wiadomość.'}
            />
          )}
        </Karta>
      )}
    </div>
  );
}

const PUSTA = { itemId: '', lotNumber: '', expiryDate: '', supplierId: '', unitCost: '', notes: '' };

function FormularzPartii({ partia, widziCeny, zamknij }: {
  partia: Partia | null; widziCeny: boolean; zamknij: () => void;
}) {
  const qc = useQueryClient();
  const edycja = useEdycjaWspolna<Partia, typeof PUSTA>({
    encja: 'partie', rekord: partia, pusty: PUSTA,
    doFormularza: (p) => ({
      itemId: String(p.item.id), lotNumber: p.lotNumber ?? '',
      expiryDate: p.expiryDate?.slice(0, 10) ?? '',
      supplierId: String(p.supplier?.id ?? ''),
      unitCost: p.unitCost ?? '', notes: p.notes ?? '',
    }),
    wczytaj: (id) => api.get<Partia>(`/partie/${id}`),
    etykiety: {
      lotNumber: 'Numer serii', expiryDate: 'Termin ważności', supplierId: 'Dostawca',
      unitCost: 'Koszt jednostkowy', notes: 'Uwagi',
    },
    pokazWartosc: (pole, v) => (pole === 'supplierId'
      ? nazwaZListy(dostawcy.pozycje, v, (d) => d.name) : undefined),
  });
  const { dane, ustaw: setDane } = edycja;
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [blad, setBlad] = useState<string | null>(null);

  const materialy = useLista({
    queryKey: ['kartoteka', 'materialy-partie'],
    queryFn: () => api.get<Lista<Indeks>>('/kartoteka?type=MATERIAL&tylkoAktywne=true&naStronie=200'),
  });
  const dostawcy = useLista({
    queryKey: ['dostawcy'], queryFn: () => api.get<{ pozycje: Dostawca[] }>('/dostawcy'),
  });

  const zapisz = useMutation({
    mutationFn: () => edycja.zapisz((d, wersja) => {
      const cialo = {
        ...d,
        itemId: Number(d.itemId),
        supplierId: d.supplierId || null,
        ...(partia ? { version: wersja } : {}),
      };
      return partia ? api.put<Partia>(`/partie/${partia.id}`, cialo) : api.post<Partia>('/partie', cialo);
    }),
    onSuccess: (zapisana) => {
      void qc.invalidateQueries({ queryKey: ['partie'] });
      edycja.poZapisie(zapisana, zamknij);
    },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        if (Object.keys(e.bledyPol).length === 0) setBlad(e.message);
      }
    },
  });

  return (
    <Karta tytul={partia ? `Partia ${partia.lotNumber ?? partia.id}` : 'Nowa partia'}>
      <form
        className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(e) => { e.preventDefault(); setBlad(null); setBledy({}); zapisz.mutate(); }}
      >
        {blad && <div className="sm:col-span-2 lg:col-span-3"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}
        <PasekWspolpracy edycja={edycja} />

        <div className="sm:col-span-2 lg:col-span-3">
          <Wybor etykieta="Materiał" value={dane.itemId} blad={bledy.itemId ?? materialy.blad}
            onChange={(e) => setDane({ ...dane, itemId: e.target.value })}
            disabled={!!partia} required>
            <option value="">— wybierz —</option>
            {materialy.pozycje.map((m) => (
              <option key={m.id} value={m.id}>
                {m.code} — {m.name}{m.isDrugDummy ? ' (atrapa leku)' : ''}
              </option>
            ))}
          </Wybor>
        </div>

        <Pole etykieta="Numer serii" value={dane.lotNumber} blad={bledy.lotNumber}
          onChange={(e) => setDane({ ...dane, lotNumber: e.target.value })}
          podpowiedz="Z opakowania producenta" />
        <Pole etykieta="Termin ważności" type="date" value={dane.expiryDate} blad={bledy.expiryDate}
          onChange={(e) => setDane({ ...dane, expiryDate: e.target.value })} />
        <Wybor etykieta="Dostawca" value={dane.supplierId} blad={bledy.supplierId ?? dostawcy.blad}
          onChange={(e) => setDane({ ...dane, supplierId: e.target.value })}>
          <option value="">— nie wskazano —</option>
          {dostawcy.pozycje.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Wybor>

        {widziCeny && (
          <Pole etykieta="Koszt jednostkowy (zł)" type="number" step="0.01" min="0"
            value={dane.unitCost} blad={bledy.unitCost}
            onChange={(e) => setDane({ ...dane, unitCost: e.target.value })}
            podpowiedz="Rzeczywista cena z tej dostawy — po niej liczymy koszt zajęć" />
        )}

        <div className="sm:col-span-2 lg:col-span-3">
          <PoleTekstowe etykieta="Uwagi" value={dane.notes} blad={bledy.notes}
            onChange={(e) => setDane({ ...dane, notes: e.target.value })} />
        </div>

        <div className="flex gap-3 sm:col-span-2 lg:col-span-3">
          <Przycisk type="submit" wariant="glowny" disabled={zapisz.isPending}>
            {zapisz.isPending ? 'Zapisywanie...' : partia ? 'Zapisz zmiany' : 'Dodaj partię'}
          </Przycisk>
          <Przycisk type="button" wariant="cichy" onClick={zamknij}>Anuluj</Przycisk>
        </div>
      </form>
    </Karta>
  );
}
